import React, { useEffect, useState, useCallback } from "react";
import { api, fmtNum, curSym, errMsg } from "@finance/lib/api";
import { toast } from "sonner";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { Label } from "@finance/components/ui/label";
import { Switch } from "@finance/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@finance/components/ui/select";
import { CurrencySelect } from "@finance/components/CurrencySelect";
import { QuickAddButton } from "@finance/components/QuickAdd";
import { Plus, Trash, Paperclip, Scan } from "@phosphor-icons/react";
import { importRollup } from "@finance/lib/importUtils";

// File attachment control for a specific box/payment; uploads directly to the transaction with a tag.
function AttachControl({ tid, tag, docs, onChanged, label = "Attach document" }) {
  const [busy, setBusy] = useState(false);
  const mine = (docs || []).filter((d) => d.document_type === tag);
  const up = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file); fd.append("document_type", tag); fd.append("transaction_id", tid); fd.append("run_extraction", "false");
    try { await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Attached"); onChanged?.(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const view = async (d) => {
    try { const r = await api.get(`/files/${d.storage_path}`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); }
    catch { toast.error("Could not open document"); }
  };
  return (
    <div className="rounded-sm border border-dashed border-gray-300 p-2">
      <label className="inline-flex cursor-pointer items-center gap-1 text-xs font-medium text-[#122E5C]" data-testid={`attach-${tag}`}>
        <Paperclip size={13} />{busy ? "Uploading…" : label}
        <input type="file" className="hidden" accept="image/*,application/pdf" onChange={(e) => { up(e.target.files[0]); e.target.value = ""; }} />
      </label>
      {mine.map((d) => (
        <div key={d.id} className="mt-1 text-xs">
          <button className="truncate text-left text-[#122E5C] hover:underline" data-testid={`attach-view-${d.id}`} onClick={() => view(d)}>{d.original_filename}</button>
        </div>
      ))}
    </div>
  );
}

function EFDBlock({ value = {}, onChange, testid }) {
  const [scanning, setScanning] = useState(false);
  const scan = async (file) => {
    if (!file) return;
    setScanning(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("document_type", "Tax_Receipt");
    try {
      const { data } = await api.post("/ocr/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const f = data.fields || {};
      onChange({
        ...value,
        code: f.tax_receipt_code || f.supplier_efd_qr_code || value.code || "",
        time: f.receipt_time || f.supplier_efd_time || value.time || "",
      });
      toast.success("EFD receipt scanned · verify the fields");
    } catch (e) { toast.error(errMsg(e) || "Could not scan receipt"); }
    finally { setScanning(false); }
  };
  return (
    <div className="grid grid-cols-2 gap-2 rounded-sm border border-emerald-200 bg-emerald-50/40 p-2">
      <div className="col-span-2 flex items-center justify-between">
        <span className="lx-label text-emerald-700">EFD (scan receipt to auto-fill)</span>
        <label className="inline-flex cursor-pointer items-center gap-1 text-xs font-medium text-emerald-700" data-testid={`${testid}-efd-scan`}>
          <Scan size={13} />{scanning ? "Scanning…" : "Scan EFD receipt"}
          <input type="file" className="hidden" accept="image/*,application/pdf" onChange={(e) => { scan(e.target.files[0]); e.target.value = ""; }} />
        </label>
      </div>
      <LabeledInput label="EFD Verification Code" testid={`${testid}-efd-code`} value={value.code} onChange={(v) => onChange({ ...value, code: v })} />
      <LabeledInput label="EFD Timestamp (TS)" placeholder="2026-06-15 14:32" testid={`${testid}-efd-time`} value={value.time} onChange={(v) => onChange({ ...value, time: v })} />
    </div>
  );
}

// Full-width stacked row for a single "Other Charge": supplier, VAT, amount, EFD + payment.
function OtherChargeRow({ oc, i, suppliers, banks, tid, docs, onChanged, onChange, onRemove }) {
  const set = (patch) => onChange({ ...oc, ...patch });
  const efdOn = !!oc.efd_applicable;
  const ocNet = Number(oc.amount) || 0;
  const ocVat = ocNet * (Number(oc.vat_rate) || 0) / 100;
  const ocTotal = ocNet + ocVat;
  const ocCur = oc.currency || "TZS";
  const ocFx = ocCur === "TZS" ? 1 : (Number(oc.fx_rate) || 1);
  const ocTotalTzs = ocTotal * ocFx;
  return (
    <div className="rounded-sm border border-gray-200 bg-gray-50/40 p-3" data-testid={`import-other-${i}`}>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-semibold text-gray-600">Charge {i + 1}</span>
        <button className="text-gray-400 hover:text-red-500" data-testid={`import-other-remove-${i}`} onClick={onRemove}><Trash size={15} /></button>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        <SupplierSelect label="Supplier (optional)" suppliers={suppliers} testid={`import-other-supplier-${i}`} value={oc.supplier_name} onChange={(v) => set({ supplier_name: v })} />
        <div>
          <Label className="lx-label">VAT</Label>
          <Select value={oc.vat_rate === undefined || oc.vat_rate === "" ? "none" : String(oc.vat_rate)} onValueChange={(v) => set({ vat_rate: v === "none" ? "" : Number(v) })}>
            <SelectTrigger className="mt-1 h-8 text-xs" data-testid={`import-other-vat-${i}`}><SelectValue placeholder="VAT" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— No VAT —</SelectItem>
              <SelectItem value="18">Standard 18%</SelectItem>
              <SelectItem value="0">Zero-rated 0%</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <LabeledInput label="Amount (net/excl.)" type="number" testid={`import-other-amount-${i}`} value={oc.amount} onChange={(v) => set({ amount: v })} />
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <div className="sm:col-span-2">
          <Label className="lx-label">Currency</Label>
          <CurrencySelect className="mt-1 h-8 text-xs" testid={`import-other-currency-${i}`} value={ocCur} onValueChange={(v) => set({ currency: v, fx_rate: v === "TZS" ? 1 : (oc.fx_rate || "") })} />
        </div>
        <div className="sm:col-span-2">
          <Label className="lx-label">FX Rate → TZS</Label>
          <Input type="number" className="mt-1 h-8 text-xs" data-testid={`import-other-fx-${i}`} disabled={ocCur === "TZS"} value={ocCur === "TZS" ? 1 : (oc.fx_rate ?? "")} onChange={(e) => set({ fx_rate: e.target.value })} />
        </div>
      </div>
      {ocNet > 0 && (
        <div className="mt-2 text-xs text-gray-500" data-testid={`import-other-preview-${i}`}>
          Net {curSym(ocCur)} {fmtNum(ocNet)} + VAT {fmtNum(ocVat)} = <span className="font-semibold text-gray-700">Total payable {curSym(ocCur)} {fmtNum(ocTotal)}</span>
          {ocCur !== "TZS" && <span className="ml-1 text-gray-400">(≈ TSh {fmtNum(ocTotalTzs)} @ {fmtNum(ocFx)})</span>}
        </div>
      )}
      <label className="mt-2 flex items-center gap-2 text-xs">
        <Switch checked={efdOn} data-testid={`import-other-efd-applicable-${i}`} onCheckedChange={(v) => set({ efd_applicable: v })} />
        EFD applicable
      </label>
      {efdOn && <div className="mt-2"><EFDBlock testid={`import-other-${i}`} value={oc.efd} onChange={(v) => set({ efd: v })} /></div>}
      <div className="mt-2">
        <PaymentBlock testid={`import-other-${i}-pay`} banks={banks} popTag={`Import_other_${i}`} tid={tid} docs={docs} onChanged={onChanged} value={oc.payment} onChange={(v) => set({ payment: v })} />
      </div>
    </div>
  );
}

// Reusable payment block, now with a Proof-of-Payment attachment and optional Confirm Payment step.
function PaymentBlock({ value = {}, banks, onChange, testid, popTag, tid, docs, onChanged, confirmable }) {
  const hasPoP = (docs || []).some((d) => d.document_type === popTag);
  return (
    <div className="grid grid-cols-4 gap-2 rounded-sm bg-gray-100/70 p-2">
      <label className="col-span-4 flex items-center gap-2 text-xs font-medium">
        <Switch checked={!!value.made} data-testid={`${testid}-made`} onCheckedChange={(v) => onChange({ ...value, made: v })} />
        Payment made
      </label>
      {value.made && <>
        <LabeledInput label="Payment Date" type="date" testid={`${testid}-date`} value={value.date} onChange={(v) => onChange({ ...value, date: v })} />
        <div>
          <Label className="lx-label">Bank</Label>
          <Select value={value.bank || "none"} onValueChange={(v) => onChange({ ...value, bank: v === "none" ? "" : v })}>
            <SelectTrigger className="mt-1 h-8 text-xs" data-testid={`${testid}-bank`}><SelectValue placeholder="Bank" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— Select bank —</SelectItem>
              {banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.bank_name} · {b.currency}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <LabeledInput label="Reference No" testid={`${testid}-ref`} value={value.ref} onChange={(v) => onChange({ ...value, ref: v })} />
        <div className="col-span-4"><AttachControl tid={tid} tag={popTag} docs={docs} onChanged={onChanged} label="Attach Proof of Payment (PoP)" /></div>
        {confirmable && (
          <label className="col-span-4 flex items-center gap-2 text-xs font-medium" data-testid={`${testid}-confirm-row`}>
            <Switch checked={!!value.confirmed} disabled={!hasPoP} data-testid={`${testid}-confirm`}
              onCheckedChange={(v) => onChange({ ...value, confirmed: v })} />
            Confirm Payment
            {!hasPoP && <span className="text-[11px] font-normal text-red-500">— attach Proof of Payment first</span>}
            {value.confirmed && hasPoP && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">Confirmed</span>}
          </label>
        )}
      </>}
    </div>
  );
}

function LabeledInput({ label, value, onChange, testid, type = "text", placeholder }) {
  return (
    <div>
      <Label className="lx-label">{label}</Label>
      <Input className="mt-1 h-8 text-sm" type={type} placeholder={placeholder} data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function SupplierSelect({ label, value, suppliers, onChange, testid }) {
  const [extra, setExtra] = useState([]);
  const all = [...(suppliers || []), ...extra.filter((e) => !(suppliers || []).some((s) => s.name === e.name))];
  return (
    <div>
      <Label className="lx-label">{label}</Label>
      <div className="mt-1 flex items-center gap-1">
        <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? "" : v)}>
          <SelectTrigger className="h-8 flex-1 text-xs" data-testid={testid}><SelectValue placeholder="Select supplier" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">— Select supplier —</SelectItem>
            {all.map((s) => <SelectItem key={s.id || s.name} value={s.name}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <QuickAddButton type="supplier" onCreated={(s) => { setExtra((x) => [...x, s]); onChange(s.name); }} />
      </div>
    </div>
  );
}

// Amount + its own Currency + FX → TZS (import charges can each be in a different currency).
function AmountCur({ section = {}, onChange, testid, label = "Amount" }) {
  const cur = section.currency || "TZS";
  const fx = cur === "TZS" ? 1 : (Number(section.fx_rate) || 1);
  const amt = Number(section.amount) || 0;
  return (
    <div>
      <div className="grid grid-cols-3 gap-2">
        <LabeledInput label={`${label} (net/excl.)`} type="number" testid={testid} value={section.amount} onChange={(v) => onChange({ amount: v })} />
        <div>
          <Label className="lx-label">Currency</Label>
          <CurrencySelect className="mt-1 h-8 text-xs" testid={`${testid}-currency`} value={cur} onValueChange={(v) => onChange({ currency: v, fx_rate: v === "TZS" ? 1 : (section.fx_rate || "") })} />
        </div>
        <div>
          <Label className="lx-label">FX Rate → TZS</Label>
          <Input type="number" className="mt-1 h-8 text-sm" data-testid={`${testid}-fx`} disabled={cur === "TZS"} value={cur === "TZS" ? 1 : (section.fx_rate ?? "")} onChange={(e) => onChange({ fx_rate: e.target.value })} />
        </div>
      </div>
      {cur !== "TZS" && amt > 0 && <div className="mt-1 text-[11px] text-gray-400" data-testid={`${testid}-tzs`}>≈ TSh {fmtNum(amt * fx)} @ {fmtNum(fx)}</div>}
    </div>
  );
}

function ItemCard({ title, applicable, onToggle, children, testid, tid, tag, docs, onChanged }) {
  const showAttach = !onToggle || applicable;
  return (
    <div className="rounded-sm border border-gray-200 p-3" data-testid={`import-item-${testid}`}>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-800">{title}</span>
        {onToggle && (
          <label className="flex items-center gap-2 text-xs text-gray-500">
            <Switch checked={!!applicable} data-testid={`import-${testid}-applicable`} onCheckedChange={onToggle} />
            Applicable
          </label>
        )}
      </div>
      {showAttach && <div className="space-y-2">
        {children}
        <AttachControl tid={tid} tag={`Import_${testid}`} docs={docs} onChanged={onChanged} />
      </div>}
    </div>
  );
}

export default function ImportDocuments({ transaction, onChanged }) {
  const tid = transaction?.id;
  const mode = transaction?.shipment_mode || "Sea";
  const docs = transaction?.documents || [];
  const [d, setD] = useState({});
  const [banks, setBanks] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [saving, setSaving] = useState(false);

  // Only reset local edits when switching to a different transaction (not on every reload).
  useEffect(() => { setD(transaction?.import_details || {}); /* eslint-disable-next-line */ }, [tid]);
  useEffect(() => {
    api.get("/bank-accounts").then((r) => setBanks(r.data)).catch(() => {});
    api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
  }, []);

  const upd = (key, patch) => setD((s) => ({ ...s, [key]: { ...(s[key] || {}), ...patch } }));
  const tra = d.tra_assessment || {};
  const traTotal = ["import_duty", "rdl", "cpf", "vat", "others"].reduce((s, k) => s + (Number(tra[k]) || 0), 0);
  const traPays = tra.payments || [];
  const traPaid = traPays.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const gclaLines = (d.gcla || {}).lines || ((d.gcla || {}).amount ? [{ amount: d.gcla.amount, currency: d.gcla.currency, fx_rate: d.gcla.fx_rate, reference: d.gcla.reference, payment: d.gcla.payment }] : []);
  const setGcla = (lines) => upd("gcla", { lines });
  const gclaTotal = gclaLines.reduce((s, l) => s + (Number(l.amount) || 0) * ((l.currency || "TZS") === "TZS" ? 1 : (Number(l.fx_rate) || 1)), 0);
  const gclaPaid = gclaLines.filter((l) => (l.payment || {}).made).length;
  const roll = importRollup(d);
  const lineItems = transaction?.line_items || [];
  const lineVals = lineItems.map((li) => Number(li.exclusive_amount) || (Number(li.qty) || 0) * (Number(li.unit_price) || 0));
  const driverTotal = lineVals.reduce((s, v) => s + v, 0);
  const alloc = lineItems.map((li, i) => {
    const lv = lineVals[i];
    const share = driverTotal > 0 ? lv / driverTotal : 0;
    const allocated = roll.landed_total * share;
    const q = Number(li.qty) || 0;
    return { lv, share, allocated, perUnit: q > 0 ? allocated / q : 0 };
  });
  const pay = (key, testid) => ({ testid, banks, popTag: `PoP_${testid}`, tid, docs, onChanged });

  const save = async () => {
    setSaving(true);
    const missing = [];
    const check = (label, p) => { if (p && p.made && !p.bank) missing.push(label); };
    check("Freight Invoice", (d.freight_invoice || {}).payment);
    check("TRA Assessment", (d.tra_assessment || {}).payment);
    check("Shipping Line", (d.shipping_line || {}).payment);
    check("GCLA", (d.gcla || {}).payment);
    check("Agent Fee", (d.agent_fee || {}).payment);
    check("Transport Charges", (d.transport || {}).payment);
    if (missing.length) toast.warning(`Payment marked as made without a bank selected: ${missing.join(", ")}`);
    try {
      await api.patch(`/transactions/${tid}/import-details`, { import_details: d });
      toast.success("Import documents saved · inventory landed cost updated");
      onChanged?.();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const other = d.other_charges || [];
  const setOther = (arr) => setD((s) => ({ ...s, other_charges: arr }));

  return (
    <div data-testid="import-documents-panel">
      <div className="mb-2 flex items-center justify-between">
        <span className="lx-label">Import Documents &amp; Costs · Shipment mode: {mode}</span>
        <Button size="sm" className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="save-import-details" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save Import Details"}</Button>
      </div>

      {/* Landed-cost rollup card */}
      <div className="mb-3 rounded-sm border border-blue-200 bg-blue-50/50 p-3" data-testid="import-rollup-card">
        <div className="lx-label mb-2 text-blue-700">Landed Cost Rollup</div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-4">
          {Object.entries(roll.components).filter(([, v]) => v > 0).map(([k, v]) => (
            <div key={k} className="flex justify-between"><span className="text-gray-500">{k}</span><span className="lx-num">{fmtNum(v)}</span></div>
          ))}
        </div>
        <div className="mt-2 flex flex-wrap justify-end gap-x-8 border-t border-blue-200 pt-2 text-sm">
          <span>Landed cost (to inventory): <span className="lx-num font-semibold" data-testid="import-landed-total">{fmtNum(roll.landed_total)}</span></span>
          <span>Grand total (incl. TRA VAT): <span className="lx-num font-bold text-[#1B4383]" data-testid="import-grand-total">{fmtNum(roll.total)}</span></span>
        </div>
        {lineItems.length > 0 && roll.landed_total > 0 && (
          <div className="mt-3 border-t border-blue-200 pt-2" data-testid="import-alloc-table">
            <div className="lx-label mb-1 text-blue-700">Per-item landed cost allocation (driver: supplier line value)</div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="lx-label text-gray-500">
                  <th className="py-1 text-left">Item</th>
                  <th className="py-1 text-right">Qty</th>
                  <th className="py-1 text-right">Unit Price</th>
                  <th className="py-1 text-right">Line Value</th>
                  <th className="py-1 text-right">Share</th>
                  <th className="py-1 text-right">Allocated (TZS)</th>
                  <th className="py-1 text-right">Landed/Unit (TZS)</th>
                </tr></thead>
                <tbody>
                  {lineItems.map((li, i) => (
                    <tr key={i} className="border-t border-blue-100" data-testid={`import-alloc-row-${i}`}>
                      <td className="py-1 pr-2">{li.description || li.item_name || `Item ${i + 1}`}</td>
                      <td className="py-1 text-right lx-num">{li.qty}</td>
                      <td className="py-1 text-right lx-num">{fmtNum(li.unit_price)}</td>
                      <td className="py-1 text-right lx-num">{fmtNum(alloc[i].lv)}</td>
                      <td className="py-1 text-right lx-num">{(alloc[i].share * 100).toFixed(1)}%</td>
                      <td className="py-1 text-right lx-num font-semibold" data-testid={`import-alloc-amount-${i}`}>{fmtNum(alloc[i].allocated)}</td>
                      <td className="py-1 text-right lx-num">{fmtNum(alloc[i].perUnit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-1 text-[11px] text-gray-400">Import charges are apportioned to each item in proportion to its supplier line value (Qty × Unit Price), then capitalized into inventory (AVCO).</div>
          </div>
        )}
      </div>

      <div className="space-y-3">
        <div className="rounded-sm border border-blue-200 bg-blue-50 p-3 text-xs text-blue-700" data-testid="import-freight-moved-note">
          <b>Freight is now entered on the Shipment.</b> A shipment usually has one freight invoice covering all its consolidated POs, so freight is captured once on the <b>Shipments</b> page (Goods in Transit) and auto-apportioned across the linked PO lines. Enter duty, agent fees and other transit charges below as before.
        </div>

        <ItemCard title="2. SADC Certificate" testid="sadc" applicable={(d.sadc || {}).applicable} onToggle={(v) => upd("sadc", { applicable: v })} tid={tid} docs={docs} onChanged={onChanged}>
          <LabeledInput label="Reference" testid="import-sadc-ref" value={(d.sadc || {}).reference} onChange={(v) => upd("sadc", { reference: v })} />
        </ItemCard>

        <ItemCard title="3. CoC (Certificate of Conformity)" testid="coc" applicable={(d.coc || {}).applicable} onToggle={(v) => upd("coc", { applicable: v })} tid={tid} docs={docs} onChanged={onChanged}>
          <LabeledInput label="Reference" testid="import-coc-ref" value={(d.coc || {}).reference} onChange={(v) => upd("coc", { reference: v })} />
        </ItemCard>

        <ItemCard title="4. TRA Assessment" testid="tra" tid={tid} docs={docs} onChanged={onChanged}>
          <div className="grid grid-cols-3 gap-2">
            <LabeledInput label="Payment Note Ref" testid="import-tra-paynote" value={tra.payment_note_ref} onChange={(v) => upd("tra_assessment", { payment_note_ref: v })} />
            <LabeledInput label="TISS Ref" testid="import-tra-tiss" value={tra.tiss_ref} onChange={(v) => upd("tra_assessment", { tiss_ref: v })} />
            <LabeledInput label="Assessment Ref" testid="import-tra-assessment" value={tra.assessment_ref} onChange={(v) => upd("tra_assessment", { assessment_ref: v })} />
          </div>
          <div className="grid grid-cols-5 gap-2">
            <LabeledInput label="IMP (Import Duty)" type="number" testid="import-tra-duty" value={tra.import_duty} onChange={(v) => upd("tra_assessment", { import_duty: v })} />
            <LabeledInput label="RDL" type="number" testid="import-tra-rdl" value={tra.rdl} onChange={(v) => upd("tra_assessment", { rdl: v })} />
            <LabeledInput label="CPF" type="number" testid="import-tra-cpf" value={tra.cpf} onChange={(v) => upd("tra_assessment", { cpf: v })} />
            <LabeledInput label="VAT" type="number" testid="import-tra-vat" value={tra.vat} onChange={(v) => upd("tra_assessment", { vat: v })} />
            <LabeledInput label="Others" type="number" testid="import-tra-others" value={tra.others} onChange={(v) => upd("tra_assessment", { others: v })} />
          </div>
          <div className="text-right text-sm">Total assessed: <span className="lx-num font-semibold" data-testid="import-tra-total">TZS {fmtNum(traTotal)}</span> · Paid: <span className={`lx-num font-semibold ${traPaid > traTotal + 0.004 ? "text-red-600" : ""}`} data-testid="import-tra-paid">TZS {fmtNum(traPaid)}</span></div>
          {traPays.map((p, i) => (
            <div key={i} className="grid grid-cols-5 items-end gap-2 rounded-sm bg-gray-100/70 p-2" data-testid={`import-tra-batch-${i}`}>
              <LabeledInput label={`Batch ${i + 1} amount (TZS)`} type="number" testid={`import-tra-batch-amt-${i}`} value={p.amount} onChange={(v) => upd("tra_assessment", { payments: traPays.map((x, j) => j === i ? { ...x, amount: v } : x) })} />
              <LabeledInput label="Date" type="date" testid={`import-tra-batch-date-${i}`} value={p.date} onChange={(v) => upd("tra_assessment", { payments: traPays.map((x, j) => j === i ? { ...x, date: v } : x) })} />
              <div>
                <Label className="lx-label">Bank</Label>
                <Select value={p.bank || "none"} onValueChange={(v) => upd("tra_assessment", { payments: traPays.map((x, j) => j === i ? { ...x, bank: v === "none" ? "" : v } : x) })}>
                  <SelectTrigger className="mt-1 h-8 text-xs" data-testid={`import-tra-batch-bank-${i}`}><SelectValue placeholder="Bank" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">— Select bank —</SelectItem>{banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.bank_name} · {b.currency}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <LabeledInput label="Reference" testid={`import-tra-batch-ref-${i}`} value={p.ref} onChange={(v) => upd("tra_assessment", { payments: traPays.map((x, j) => j === i ? { ...x, ref: v } : x) })} />
              <Button type="button" size="sm" variant="outline" data-testid={`import-tra-batch-remove-${i}`} onClick={() => upd("tra_assessment", { payments: traPays.filter((_, j) => j !== i) })}>Remove</Button>
            </div>
          ))}
          <Button type="button" size="sm" variant="outline" data-testid="import-tra-add-payment" disabled={traPaid >= traTotal} onClick={() => upd("tra_assessment", { payments: [...traPays, { amount: Math.max(0, traTotal - traPaid).toFixed(2), date: "", bank: "", ref: "" }] })}>+ Add payment</Button>
          {!traPays.length && tra.payment?.made && <div className="text-xs text-gray-500">Legacy single payment recorded — add batch rows to replace it.</div>}
        </ItemCard>

        <ItemCard title="5. TBS" testid="tbs" applicable={(d.tbs || {}).applicable} onToggle={(v) => upd("tbs", { applicable: v })} tid={tid} docs={docs} onChanged={onChanged}>
          <LabeledInput label="Reference" testid="import-tbs-ref" value={(d.tbs || {}).reference} onChange={(v) => upd("tbs", { reference: v })} />
          <AmountCur section={d.tbs || {}} onChange={(patch) => upd("tbs", patch)} testid="import-tbs-amount" />
          <PaymentBlock {...pay("tbs", "import-tbs-pay")} confirmable value={(d.tbs || {}).payment} onChange={(v) => upd("tbs", { payment: v })} />
        </ItemCard>

        {mode === "Air" && (
          <ItemCard title="6. Swissport (air freight)" testid="swissport" tid={tid} docs={docs} onChanged={onChanged}>
            <LabeledInput label="Reference" testid="import-swissport-ref" value={(d.swissport || {}).reference} onChange={(v) => upd("swissport", { reference: v })} />
            <AmountCur section={d.swissport || {}} onChange={(patch) => upd("swissport", patch)} testid="import-swissport-amount" />
            <PaymentBlock {...pay("swissport", "import-swissport-pay")} value={(d.swissport || {}).payment} onChange={(v) => upd("swissport", { payment: v })} />
          </ItemCard>
        )}

        <ItemCard title="7. Bill of Lading" testid="bol" tid={tid} docs={docs} onChanged={onChanged}>
          <LabeledInput label="Reference" testid="import-bol-ref" value={(d.bill_of_lading || {}).reference} onChange={(v) => upd("bill_of_lading", { reference: v })} />
        </ItemCard>

        <ItemCard title="7b. Packing List" testid="packing" tid={tid} docs={docs} onChanged={onChanged}>
          <label className="flex items-center gap-2 text-xs">
            <Switch checked={!!(d.packing_list || {}).received} data-testid="import-packing-received" onCheckedChange={(v) => upd("packing_list", { received: v })} />
            Packing List received
          </label>
          <LabeledInput label="Reference (optional)" testid="import-packing-ref" value={(d.packing_list || {}).reference} onChange={(v) => upd("packing_list", { reference: v })} />
        </ItemCard>

        {mode === "Sea" && (
          <ItemCard title="8. Shipping Line Docs (sea)" testid="shipping" tid={tid} docs={docs} onChanged={onChanged}>
            <LabeledInput label="Reference" testid="import-shipping-ref" value={(d.shipping_line || {}).reference} onChange={(v) => upd("shipping_line", { reference: v })} />
            <SupplierSelect label="Shipping Line Supplier" suppliers={suppliers} testid="import-shipping-supplier" value={(d.shipping_line || {}).supplier_name} onChange={(v) => upd("shipping_line", { supplier_name: v })} />
            <AmountCur section={d.shipping_line || {}} onChange={(patch) => upd("shipping_line", patch)} testid="import-shipping-amount" />
            <PaymentBlock {...pay("shipping_line", "import-shipping-pay")} value={(d.shipping_line || {}).payment} onChange={(v) => upd("shipping_line", { payment: v })} />
            <EFDBlock testid="import-shipping" value={(d.shipping_line || {}).efd} onChange={(v) => upd("shipping_line", { efd: v })} />
          </ItemCard>
        )}

        {mode === "Sea" && (
          <ItemCard title="9. Wharfage (sea)" testid="wharfage" tid={tid} docs={docs} onChanged={onChanged}>
            <LabeledInput label="Reference" testid="import-wharfage-ref" value={(d.wharfage || {}).reference} onChange={(v) => upd("wharfage", { reference: v })} />
            <AmountCur section={d.wharfage || {}} onChange={(patch) => upd("wharfage", patch)} testid="import-wharfage-amount" />
            <PaymentBlock {...pay("wharfage", "import-wharfage-pay")} confirmable value={(d.wharfage || {}).payment} onChange={(v) => upd("wharfage", { payment: v })} />
          </ItemCard>
        )}

        <ItemCard title="10. GCLA" testid="gcla" applicable={(d.gcla || {}).applicable} onToggle={(v) => upd("gcla", { applicable: v })} tid={tid} docs={docs} onChanged={onChanged}>
          <LabeledInput label="Reference" testid="import-gcla-ref" value={(d.gcla || {}).reference} onChange={(v) => upd("gcla", { reference: v })} />
          <div className="text-sm" data-testid="import-gcla-total">Total: <span className="lx-num font-semibold">TZS {fmtNum(gclaTotal)}</span> · {gclaLines.length ? `${gclaPaid}/${gclaLines.length} paid${gclaPaid && gclaPaid < gclaLines.length ? " (partial)" : ""}` : "no charges"}</div>
          {gclaLines.map((l, i) => (
            <div key={i} className="space-y-2 rounded-sm border border-gray-200 p-2" data-testid={`import-gcla-line-${i}`}>
              <div className="grid grid-cols-4 gap-2">
                <AmountCur section={l} onChange={(patch) => setGcla(gclaLines.map((x, j) => j === i ? { ...x, ...patch } : x))} testid={`import-gcla-amount-${i}`} />
                <LabeledInput label="Reference" testid={`import-gcla-line-ref-${i}`} value={l.reference} onChange={(v) => setGcla(gclaLines.map((x, j) => j === i ? { ...x, reference: v } : x))} />
                <LabeledInput label="Date" type="date" testid={`import-gcla-line-date-${i}`} value={l.date} onChange={(v) => setGcla(gclaLines.map((x, j) => j === i ? { ...x, date: v } : x))} />
                <LabeledInput label="Note" testid={`import-gcla-line-note-${i}`} value={l.note} onChange={(v) => setGcla(gclaLines.map((x, j) => j === i ? { ...x, note: v } : x))} />
              </div>
              <PaymentBlock {...pay("gcla", `import-gcla-pay-${i}`)} value={l.payment || {}} onChange={(v) => setGcla(gclaLines.map((x, j) => j === i ? { ...x, payment: v } : x))} />
              <Button type="button" size="sm" variant="outline" data-testid={`import-gcla-remove-${i}`} onClick={() => setGcla(gclaLines.filter((_, j) => j !== i))}>Remove charge</Button>
            </div>
          ))}
          <Button type="button" size="sm" variant="outline" data-testid="import-gcla-add" onClick={() => setGcla([...gclaLines, { amount: "", currency: "TZS", payment: {} }])}>+ Add GCLA charge</Button>
        </ItemCard>

        <ItemCard title="11. Release Order" testid="release" tid={tid} docs={docs} onChanged={onChanged}>
          <LabeledInput label="Reference" testid="import-release-ref" value={(d.release_order || {}).reference} onChange={(v) => upd("release_order", { reference: v })} />
        </ItemCard>

        <ItemCard title="12. Agent Fee" testid="agent" tid={tid} docs={docs} onChanged={onChanged}>
          <SupplierSelect label="Agent Name" suppliers={suppliers} testid="import-agent-name" value={(d.agent_fee || {}).agent_name} onChange={(v) => upd("agent_fee", { agent_name: v })} />
          <LabeledInput label="Tax Invoice No" testid="import-agent-taxinv" value={(d.agent_fee || {}).tax_invoice_no} onChange={(v) => upd("agent_fee", { tax_invoice_no: v })} />
          <AmountCur section={d.agent_fee || {}} onChange={(patch) => upd("agent_fee", patch)} testid="import-agent-amount" />
          <EFDBlock testid="import-agent" value={(d.agent_fee || {}).efd} onChange={(v) => upd("agent_fee", { efd: v })} />
          <PaymentBlock {...pay("agent_fee", "import-agent-pay")} value={(d.agent_fee || {}).payment} onChange={(v) => upd("agent_fee", { payment: v })} />
        </ItemCard>

        <ItemCard title="13. Transport Charges" testid="transport" applicable={(d.transport || {}).applicable} onToggle={(v) => upd("transport", { applicable: v })} tid={tid} docs={docs} onChanged={onChanged}>
          <AmountCur section={d.transport || {}} onChange={(patch) => upd("transport", patch)} testid="import-transport-amount" />
          <PaymentBlock {...pay("transport", "import-transport-pay")} value={(d.transport || {}).payment} onChange={(v) => upd("transport", { payment: v })} />
        </ItemCard>

        <div className="rounded-sm border border-gray-200 p-3" data-testid="import-item-other">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-800">14. Other Charges</span>
            <Button size="sm" variant="outline" data-testid="import-other-add" onClick={() => setOther([...other, { amount: "" }])}><Plus size={14} className="mr-1" />Add charge</Button>
          </div>
          {other.length === 0 && <div className="text-xs text-gray-400">No other charges. Add a charge with expense, supplier, VAT, amount, EFD and payment if needed.</div>}
          <div className="space-y-3">
            {other.map((oc, i) => (
              <OtherChargeRow key={i} oc={oc} i={i} suppliers={suppliers} banks={banks}
                tid={tid} docs={docs} onChanged={onChanged}
                onChange={(v) => { const a = [...other]; a[i] = v; setOther(a); }}
                onRemove={() => setOther(other.filter((_, j) => j !== i))} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
